export const ErrorCodes = {
  Profile: {
    MISSING_USER_PROFILE_IN_REQUEST: 'SSAR_PRO_MUIR',
  },
  Auth: {
    SESSION_TIMED_OUT: 'SSAR_SESSION_TO',
    SESSION_INVALIDATED: 'SSAR_SESSION_INV',
  },
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type TechnicalDetails = string | string[] | Record<string, any>;

export interface SSARErrorLike extends Error {
  statusCode: number;
  technicalDetails: TechnicalDetails;
  details?: string | string[];
  errorCode?: string;
  stack?: string;
  baseType: string;
  id?: string;
}

export class CancelOperationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CancelOperationError';
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

export abstract class SSARError extends Error implements SSARErrorLike {
  statusCode: number;
  id?: string;
  technicalDetails: TechnicalDetails;
  details?: string | string[];
  errorCode?: string;
  public static readonly baseType = 'SSARError';
  baseType: string = SSARError.baseType;
  stack?: string;
  constructor(
    message: string,
    statusCode: number,
    technicalDetails: TechnicalDetails = [],
    details?: string | string[],
    errorCode?: string,
  ) {
    super(message);
    this.name = this.constructor.name;
    this.errorCode = errorCode;
    this.message = message;
    this.statusCode = statusCode;
    this.technicalDetails = technicalDetails;
    this.details = details;
    if (typeof Error.captureStackTrace === 'function') {
      Error.captureStackTrace(this, this.constructor);
    } else {
      this.stack = new Error(message).stack;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static isSSARError(err: any): err is SSARError {
    return err?.baseType === SSARError.baseType;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  toJSON(): any {
    return {
      resultCode: -1,
      statusCode: this.statusCode,
      name: this.name,
      baseType: this.baseType,
      _type: this.name,
      message: this.message,
      technicalDetails: this.technicalDetails,
      details: this.details,
      stack: this.stack,
      errorCode: this.errorCode,
      id: this.id,
    };
  }
}

export class DataNotFoundError extends SSARError {
  constructor(
    message: string,
    statusCode = 400,
    technicalDetails: TechnicalDetails = [],
    details?: string | string[],
  ) {
    super(message, statusCode, technicalDetails, details);
  }
}

export class PayloadTooLargeError extends SSARError {
  constructor(
    message: string,
    technicalDetails: TechnicalDetails = [],
    details?: string | string[],
  ) {
    super(message, 413, technicalDetails, details);
  }
}

export class BadRequestError extends SSARError {
  constructor(
    message: string,
    technicalDetails: TechnicalDetails = [],
    details?: string | string[],
  ) {
    super(message, 400, technicalDetails, details);
  }
}

export class InternalServerError extends SSARError {
  constructor(
    message: string,
    technicalDetails: TechnicalDetails = [],
    details?: string | string[],
  ) {
    super(message, 500, technicalDetails, details);
  }
}

export class ResourceNotFoundError extends SSARError {
  constructor(
    message: string,
    technicalDetails: TechnicalDetails = [],
    details?: string | string[],
  ) {
    super(message, 404, technicalDetails, details);
  }
}

export class InvalidInputDataError extends SSARError {
  constructor(
    message: string,
    statusCode = 400,
    technicalDetails: TechnicalDetails = [],
    details: string | string[] = [],
  ) {
    super(message, statusCode, technicalDetails, details);
  }
}

export class MissingUserProfileInRequestError extends SSARError {
  constructor(
    message: string,
    technicalDetails: TechnicalDetails = [],
    details?: string | string[],
  ) {
    super(
      message,
      401,
      technicalDetails,
      details,
      ErrorCodes.Profile.MISSING_USER_PROFILE_IN_REQUEST,
    );
  }
}

export class InvalidCSRFTokenError extends SSARError {
  constructor(
    message: string,
    technicalDetails: TechnicalDetails = [],
    details?: string | string[],
  ) {
    super(message, 500, technicalDetails, details);
  }
}

export abstract class AuthError extends SSARError {
  constructor(
    message: string,
    statusCode: 401 | 403,
    technicalDetails: TechnicalDetails = [],
    details?: string | string[],
  ) {
    super(message, statusCode, technicalDetails, details);
  }
}

export class InvalidAuthTokenError extends AuthError {
  constructor(
    message: string,
    technicalDetails: TechnicalDetails = [],
    details?: string | string[],
  ) {
    super(message, 401, technicalDetails, details);
  }
}
export class AuthTokenNotFoundError extends AuthError {
  constructor(
    message: string,
    technicalDetails: TechnicalDetails = [],
    details?: string | string[],
  ) {
    super(message, 401, technicalDetails, details);
  }
}

export class PermissionError extends AuthError {
  constructor(
    message: string,
    technicalDetails: TechnicalDetails = [],
    details?: string | string[],
  ) {
    super(message, 403, technicalDetails, details);
  }
}

export class SessionTimedOutError extends AuthError {
  constructor(
    message: string,
    technicalDetails: TechnicalDetails = [],
    details?: string | string[],
  ) {
    super(message, 401, technicalDetails, details);
  }
}

export class InvalidSessionError extends AuthError {
  constructor(
    message: string,
    technicalDetails: TechnicalDetails = [],
    details?: string | string[],
  ) {
    super(message, 401, technicalDetails, details);
  }
}

export class TnCError extends SSARError {
  constructor(
    message: string,
    statusCode: number,
    technicalDetails: TechnicalDetails = [],
    details?: string | string[],
  ) {
    super(message, statusCode, technicalDetails, details);
  }
}

export class JobError extends SSARError {
  constructor(
    message: string,
    statusCode: number,
    technicalDetails: TechnicalDetails = [],
    details?: string | string[],
  ) {
    super(message, statusCode, technicalDetails, details);
  }
}

export class FileUploadError extends SSARError {
  constructor(
    message: string,
    technicalDetails: TechnicalDetails = [],
    details?: string | string[],
  ) {
    super(message, 500, technicalDetails, details);
  }
}

export class DraftError extends SSARError {
  constructor(
    message: string,
    statusCode: number,
    technicalDetails: TechnicalDetails = [],
    details?: string | string[],
  ) {
    super(message, statusCode, technicalDetails, details);
  }
}

export class ProgramError extends SSARError {
  constructor(
    message: string,
    statusCode: number,
    technicalDetails: TechnicalDetails = [],
    details?: string | string[],
  ) {
    super(message, statusCode, technicalDetails, details);
  }
}

export class InvalidProgramInputError extends SSARError {
  constructor(
    message: string,
    technicalDetails: TechnicalDetails = [],
    details: string | string[],
  ) {
    super(message, 400, technicalDetails, details);
  }
}

export class GenericApplicationError extends SSARError {
  constructor(
    message: string,
    statusCode: number,
    technicalDetails: TechnicalDetails = [],
    details?: string | string[],
  ) {
    super(message, statusCode, technicalDetails, details);
  }
}

export class SummaryError extends SSARError {
  constructor(
    message: string,
    statusCode: number,
    technicalDetails: TechnicalDetails = [],
    details?: string | string[],
  ) {
    super(message, statusCode, technicalDetails, details);
  }
}

export class MongoDBError extends SSARError {
  constructor(
    message: string,
    statusCode: number,
    technicalDetails: TechnicalDetails = [],
    details?: string | string[],
  ) {
    super(message, statusCode, technicalDetails, details);
  }
}

export class ApiGatewayError extends SSARError {
  constructor(
    message: string,
    statusCode: number,
    technicalDetails: TechnicalDetails = [],
    details?: string | string[],
  ) {
    super(message, statusCode, technicalDetails, details);
  }
}

export class ApiGatewayConnectivityError extends ApiGatewayError {
  constructor(
    message: string,
    statusCode: number,
    technicalDetails: TechnicalDetails = [],
    details?: string | string[],
  ) {
    super(message, statusCode, technicalDetails, details);
  }
}

export class ApiGatewayRequestProcessingError extends ApiGatewayError {
  constructor(
    message: string,
    statusCode: number,
    technicalDetails: TechnicalDetails = [],
    details?: string | string[],
  ) {
    super(message, statusCode, technicalDetails, details);
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const convertToSSARError = (error: any): SSARError => {
  // logger.error(error);
  if (SSARError.isSSARError(error)) {
    return error;
  } else {
    // console.log(error);
    return new GenericApplicationError(
      error?.message
        ? error?.message
        : error?.messages
        ? (error?.messages || []).join('\n')
        : error?.response?.data?.Message ||
          error?.response?.statusText ||
          error?.statusText ||
          error?.status ||
          'Unknown error.',
      error?.response?.status || error?.status || 500,
      error?.stack,
    );
  }
};

export { convertToSSARError };
